/**
 * Iter131 — Super-Admin WhatsApp / Meta System-Konfiguration
 *
 * Zentrale Meta-Zugangsdaten (Access Token, Phone Number ID, Verify Token,
 * App Secret) — systemweit, damit die App überhaupt WhatsApp-Nachrichten
 * empfangen und senden kann. Das ist NICHT die User-Nummer-Verknüpfung,
 * sondern die technische Meta-Business-Konfiguration.
 */
import React, { useEffect, useState } from "react";
import { formatDateTime } from "@/lib/formatDate";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, ExternalLink, MessageCircle, Info } from "lucide-react";

export default function WhatsAppSystemConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [sendPhone, setSendPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [tplCatalog, setTplCatalog] = useState(null);
  const [tplSubmitting, setTplSubmitting] = useState(false);
  const [tplResults, setTplResults] = useState(null);
  const [metaStatus, setMetaStatus] = useState(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const loadMetaStatus = async () => {
    setMetaLoading(true);
    try {
      const r = await apiClient.get("/admin/whatsapp/templates/meta-status");
      setMetaStatus(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status bei Meta nicht abrufbar");
    } finally {
      setMetaLoading(false);
    }
  };
  const [outboundLog, setOutboundLog] = useState(null);
  const [form, setForm] = useState({
    phone_number_id: "",
    access_token: "",
    verify_token: "",
    app_secret: "",
    business_phone_display: "",
    waba_id: "",
    onboarding_template_name: "",
    onboarding_template_lang: "de",
    payslip_template_name: "",
    payslip_template_lang: "de",
  });
  const [alertPhones, setAlertPhones] = useState("");
  const [savingAlert, setSavingAlert] = useState(false);

  const saveAlertPhones = async () => {
    setSavingAlert(true);
    try {
      const list = alertPhones.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean);
      const r = await apiClient.put("/whatsapp/system-config", { alert_phones: list });
      setCfg(r.data);
      setAlertPhones((r.data.alert_phones || []).join(", "));
      toast.success(`${(r.data.alert_phones || []).length} Alarm-Nummer(n) gespeichert`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSavingAlert(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/whatsapp/system-config");
      setCfg(r.data);
      setAlertPhones((r.data.alert_phones || []).join(", "));
      // Iter150: Alle Formularfelder leer lassen — bestehende Werte
      // sind oben maskiert sichtbar. Leere Felder = "bleibt wie es ist".
      // Das macht Partial-Save (nur 1 Feld aktualisieren) glasklar.
      setForm({
        phone_number_id: "",
        access_token: "",
        verify_token: "",
        app_secret: "",
        business_phone_display: "",
        waba_id: "",
        onboarding_template_name: "",
        onboarding_template_lang: "de",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Konfiguration nicht laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    apiClient.get("/whatsapp/templates/catalog").then(r => setTplCatalog(r.data)).catch(() => {});
    apiClient.get("/whatsapp/outbound-log?limit=15").then(r => setOutboundLog(r.data.entries)).catch(() => {});
  }, []);

  const handleSave = async () => {
    // Iter147: Partial-Save erlaubt — mindestens 1 Feld muss gesetzt sein.
    // Meta erlaubt die Webhook-Registrierung erst wenn der Verify-Token
    // bereits im Backend liegt, deshalb muss Verify-Token einzeln gespeichert
    // werden können.
    const anyFilled = form.phone_number_id || form.access_token || form.verify_token || form.app_secret || form.waba_id || form.onboarding_template_name || form.payslip_template_name;
    if (!anyFilled) {
      toast.error("Bitte mindestens ein Feld ausfüllen.");
      return;
    }
    setSaving(true);
    try {
      // Nur nicht-leere Felder senden (spart Bytes, macht Debug klarer)
      // Iter150: business_phone_display auch nur senden wenn nicht leer,
      // sonst würde eine reine Access-Token-Änderung die Anzeige-Nummer löschen.
      const payload = {};
      if (form.phone_number_id) payload.phone_number_id = form.phone_number_id;
      if (form.access_token) payload.access_token = form.access_token;
      if (form.verify_token) payload.verify_token = form.verify_token;
      if (form.app_secret) payload.app_secret = form.app_secret;
      if (form.waba_id) payload.waba_id = form.waba_id;
      if (form.business_phone_display) payload.business_phone_display = form.business_phone_display;
      if (form.onboarding_template_name) { payload.onboarding_template_name = form.onboarding_template_name; payload.onboarding_template_lang = form.onboarding_template_lang || "de"; }
      if (form.payslip_template_name) { payload.payslip_template_name = form.payslip_template_name; payload.payslip_template_lang = form.payslip_template_lang || "de"; }
      await apiClient.put("/whatsapp/system-config", payload);
      const filledLabels = [
        form.phone_number_id && "Phone Number ID",
        form.access_token && "Access Token",
        form.verify_token && "Verify Token",
        form.app_secret && "App Secret",
        form.waba_id && "WABA-ID",
      ].filter(Boolean).join(" · ");
      toast.success(`Gespeichert: ${filledLabels || "Config"}`);
      setForm((prev) => ({ ...prev, access_token: "", verify_token: "", app_secret: "" }));
      setTestResult(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await apiClient.post("/whatsapp/system-config/test");
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, detail: e.response?.data?.detail || "Test fehlgeschlagen" });
    } finally {
      setTesting(false);
    }
  };

  const handleTestSend = async (mode = "text") => {
    const phone = sendPhone.trim();
    if (!phone.startsWith("+") || phone.length < 9) {
      toast.error("Bitte Nummer im Format +41761234567 eingeben.");
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const r = await apiClient.post("/whatsapp/system-config/test-send", { phone, mode });
      setSendResult(r.data);
      if (r.data?.ok) toast.success(r.data.detail || "Meta hat die Nachricht angenommen.");
      else toast.error(r.data?.detail || "Versand fehlgeschlagen");
    } catch (e) {
      const detail = e.response?.data?.detail || "Versand fehlgeschlagen";
      setSendResult({ ok: false, detail });
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const webhookBase = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
  const webhookUrl = `${webhookBase}/api/whatsapp/webhook`;

  return (
    <div className="space-y-4">
      <Card className="p-5" data-testid="wa-system-config">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Lade Konfiguration …
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-emerald-600" />
                <h2 className="text-lg font-semibold text-slate-900">Meta WhatsApp Business — System-Konfiguration</h2>
              </div>
              <span
                className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                  cfg?.configured
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-slate-50 text-slate-500 border-slate-200"
                }`}
                data-testid="wa-config-status"
              >
                {cfg?.configured ? "✓ Konfiguriert" : "Nicht konfiguriert"}
              </span>
            </div>

            {cfg?.configured && (
              <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs bg-slate-50 border border-slate-200 rounded p-3">
                <div>
                  <span className="text-slate-500">Phone Number ID: </span>
                  <span className="font-mono text-slate-800">{cfg.phone_number_id || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-500">Business Nummer: </span>
                  <span className="font-mono text-slate-800">{cfg.business_phone_display || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-500">Access Token: </span>
                  <span className="font-mono text-slate-800">{cfg.access_token_masked || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-500">Verify Token: </span>
                  <span className="font-mono text-slate-800">{cfg.verify_token_masked || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-500">App Secret: </span>
                  <span className="font-mono text-slate-800">{cfg.app_secret_masked || "—"}</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 leading-relaxed">
                💡 <strong>Einzelnes Feld aktualisieren:</strong> Nur das Feld ausfüllen, das du ändern willst — alle anderen Felder <em>leer lassen</em>. Bestehende Werte bleiben dann erhalten. Nichts wird überschrieben.
              </div>
              <div>
                <Label htmlFor="phone_number_id">Phone Number ID {!cfg?.configured && "*"}</Label>
                <Input
                  id="phone_number_id"
                  value={form.phone_number_id}
                  onChange={(e) => setForm({ ...form, phone_number_id: e.target.value.trim() })}
                  placeholder={cfg?.configured ? `Leer lassen = ${cfg.phone_number_id || "aktuell"} bleibt` : "z.B. 123456789012345"}
                  data-testid="wa-phone-number-id"
                />
              </div>
              <div>
                <Label htmlFor="business_phone_display">Business-Nummer (Anzeige)</Label>
                <Input
                  id="business_phone_display"
                  value={form.business_phone_display}
                  onChange={(e) => setForm({ ...form, business_phone_display: e.target.value })}
                  placeholder={cfg?.configured && cfg.business_phone_display ? `Leer lassen = ${cfg.business_phone_display} bleibt` : "z.B. +41 79 000 00 00"}
                  data-testid="wa-business-phone"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="onboarding_template_name">Onboarding-Vorlage (Meta-Template, für Nachrichten ausserhalb des 24h-Fensters)</Label>
                <div className="flex gap-2">
                  <Input
                    id="onboarding_template_name"
                    value={form.onboarding_template_name}
                    onChange={(e) => setForm({ ...form, onboarding_template_name: e.target.value })}
                    placeholder={cfg?.onboarding_template_name ? `Leer lassen = «${cfg.onboarding_template_name}» bleibt` : "z.B. mitarbeiter_onboarding"}
                    data-testid="wa-onboarding-template"
                  />
                  <Input className="w-20" value={form.onboarding_template_lang} onChange={(e) => setForm({ ...form, onboarding_template_lang: e.target.value })} placeholder="de" data-testid="wa-onboarding-template-lang" />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Platzhalter in dieser Reihenfolge: {"{{1}}"} Vorname · {"{{2}}"} Firma · {"{{3}}"} Link · {"{{4}}"} Gültigkeit in Tagen. Ohne Vorlage wird eine Freitext-Nachricht versucht (nur möglich, wenn der Mitarbeiter in den letzten 24h geschrieben hat).</p>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="payslip_template_name">Lohnabrechnungs-Vorlage (Meta-Template mit Dokument-Header)</Label>
                <div className="flex gap-2">
                  <Input
                    id="payslip_template_name"
                    value={form.payslip_template_name}
                    onChange={(e) => setForm({ ...form, payslip_template_name: e.target.value })}
                    placeholder={cfg?.payslip_template_name ? `Leer lassen = «${cfg.payslip_template_name}» bleibt` : "z.B. lohnabrechnung_pdf"}
                    data-testid="wa-payslip-template"
                  />
                  <Input className="w-20" value={form.payslip_template_lang} onChange={(e) => setForm({ ...form, payslip_template_lang: e.target.value })} placeholder="de" data-testid="wa-payslip-template-lang" />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Header: Dokument (PDF) · Body-Platzhalter: {"{{1}}"} Vorname · {"{{2}}"} Periode · {"{{3}}"} Firma · {"{{4}}"} Nettolohn. Ohne Vorlage wird das PDF als Dokument-Nachricht gesendet (24h-Fenster), sonst ein Text mit Download-Link.</p>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="access_token">Access Token {!cfg?.configured && "*"}</Label>
                <Input
                  id="access_token"
                  type="password"
                  value={form.access_token}
                  onChange={(e) => setForm({ ...form, access_token: e.target.value.trim() })}
                  placeholder={cfg?.configured ? `Leer lassen = ${cfg.access_token_masked || "aktueller Token"} bleibt` : "EAAxxxxx…"}
                  data-testid="wa-access-token"
                />
                <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 p-2.5 text-[11px] leading-relaxed text-rose-900">
                  ⚠️ <strong>Achtung — Meta-Falle:</strong> Der Token aus <em>Meta Developer → App → WhatsApp → API Setup</em> läuft <strong>trotz Beschriftung &quot;permanent&quot; nach 24 h ab</strong>. Für einen wirklich permanenten Token brauchst du einen <strong>System-User-Token</strong>:
                  <ol className="list-decimal list-inside mt-1 space-y-0.5">
                    <li><a className="underline font-medium" href="https://business.facebook.com/settings/system-users" target="_blank" rel="noreferrer">business.facebook.com → System-Nutzer</a> anlegen (Rolle: Admin)</li>
                    <li>WhatsApp-App + WABA-Konto zum System-Nutzer zuweisen</li>
                    <li>Token generieren — <strong>Ablauf: &quot;Läuft nie ab&quot;</strong> — mit Scopes <code>whatsapp_business_management</code> + <code>whatsapp_business_messaging</code></li>
                  </ol>
                </div>
              </div>
              <div>
                <Label htmlFor="verify_token">Verify Token</Label>
                <Input
                  id="verify_token"
                  type="password"
                  value={form.verify_token}
                  onChange={(e) => setForm({ ...form, verify_token: e.target.value.trim() })}
                  placeholder={cfg?.verify_token_masked ? `Leer lassen = ${cfg.verify_token_masked} bleibt` : "Selbst gewählte Zeichenkette (min. 8)"}
                  data-testid="wa-verify-token"
                />
                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                  💡 Kann einzeln gespeichert werden — Meta prüft ihn zuerst gegen unseren Webhook.
                </p>
              </div>
              <div>
                <Label htmlFor="app_secret">App Secret</Label>
                <Input
                  id="app_secret"
                  type="password"
                  value={form.app_secret}
                  onChange={(e) => setForm({ ...form, app_secret: e.target.value.trim() })}
                  placeholder={cfg?.app_secret_masked ? `Leer lassen = ${cfg.app_secret_masked} bleibt` : "Aus Meta App-Dashboard"}
                  data-testid="wa-app-secret"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="waba_id">WhatsApp Business Account ID (WABA-ID)</Label>
                <Input
                  id="waba_id"
                  value={form.waba_id}
                  onChange={(e) => setForm({ ...form, waba_id: e.target.value.trim() })}
                  placeholder={cfg?.waba_id ? `Leer lassen = ${cfg.waba_id} bleibt` : "z.B. 102030405060708"}
                  data-testid="wa-waba-id"
                />
                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                  💡 Zu finden im <a className="underline" href="https://business.facebook.com/settings/whatsapp-business-accounts" target="_blank" rel="noreferrer">Meta Business Manager → WhatsApp-Konten</a> — nötig, um die Message-Templates per Knopfdruck einzureichen.
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50/60 p-3" data-testid="wa-alert-phones-box">
              <Label htmlFor="alert_phones" className="text-rose-900">🚨 Störungsalarm per WhatsApp – Nummern der Projektleitung</Label>
              <p className="text-[11px] text-rose-800/80 mt-0.5 mb-2 leading-relaxed">
                Der Integrations-Wächter sendet Störungen, Warnungen und Entwarnungen als Meta-Vorlage <code>system_stoerung</code> an diese Nummern
                (E.164, mehrere mit Komma). Die Vorlage wird mit «Vorlagen bei Meta einreichen» hochgeladen.
              </p>
              <div className="flex gap-2">
                <Input
                  id="alert_phones"
                  value={alertPhones}
                  onChange={(e) => setAlertPhones(e.target.value)}
                  placeholder="+41791234567, +41765551234"
                  data-testid="wa-alert-phones"
                />
                <Button onClick={saveAlertPhones} disabled={savingAlert} variant="outline" className="shrink-0" data-testid="wa-alert-phones-save">
                  {savingAlert ? "Speichert…" : "Nummern speichern"}
                </Button>
              </div>
              {cfg?.alert_phones?.length > 0 && (
                <p className="text-[11px] text-rose-900 mt-1.5" data-testid="wa-alert-phones-current">Aktiv: {cfg.alert_phones.join(", ")}</p>
              )}
            </div>

            <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/70 p-2.5 text-[11px] text-indigo-900 leading-relaxed">
              🔑 <strong>Meta-Setup-Reihenfolge:</strong>
              1) <strong>Verify Token</strong> hier eintragen & speichern.
              2) Bei Meta unter <em>Webhooks konfigurieren</em>: Callback-URL <code className="bg-white px-1 rounded">{webhookUrl}</code> + denselben Token eintragen → <em>Bestätigen & speichern</em>.
              3) Erst danach die anderen 3 Felder (Phone Number ID / Access Token / App Secret) hier speichern.
            </div>

            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-save-btn">
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                Speichern
              </Button>
              <Button onClick={handleTest} disabled={testing || !cfg?.configured} variant="outline" data-testid="wa-test-btn">
                {testing && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                Live-Test gegen Meta
              </Button>
              <Button
                variant="outline"
                disabled={!cfg?.configured || tplSubmitting}
                data-testid="wa-submit-templates-btn"
                onClick={async () => {
                  if (!cfg?.waba_id && !form.waba_id) {
                    toast.error("Bitte zuerst die WABA-ID im Feld oben eintragen und speichern.");
                    return;
                  }
                  setTplSubmitting(true);
                  setTplResults(null);
                  try {
                    const r = await apiClient.post("/admin/whatsapp/templates/submit-to-meta", {
                      waba_id: form.waba_id || null,
                    });
                    setTplResults(r.data);
                    if (r.data.submitted_or_existing === r.data.total) toast.success(r.data.message);
                    else toast.warning(r.data.message);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Einreichen fehlgeschlagen");
                  } finally {
                    setTplSubmitting(false);
                  }
                }}
              >
                {tplSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                Alle Vorlagen an Meta senden
              </Button>
              {testResult && (
                <span
                  className={`text-xs flex items-center gap-1.5 ${testResult.ok ? "text-emerald-700" : "text-rose-700"}`}
                  data-testid="wa-test-result"
                >
                  {testResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  {testResult.detail}
                </span>
              )}
            </div>

            {/* Iter256: Ergebnis der Template-Einreichung bei Meta */}
            {tplResults && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="wa-template-submit-results">
                <div className="text-xs font-semibold text-slate-700 mb-2">{tplResults.message}</div>
                <div className="space-y-1">
                  {tplResults.results.map((x) => (
                    <div key={x.template} className="flex items-center gap-2 text-[12px]" data-testid={`wa-tpl-result-${x.template}`}>
                      {x.status === "submitted" && <span className="text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">eingereicht ✓</span>}
                      {x.status === "exists" && <span className="text-slate-600 bg-slate-200 rounded-full px-2 py-0.5">bereits vorhanden</span>}
                      {x.status === "failed" && <span className="text-red-700 bg-red-100 rounded-full px-2 py-0.5">fehlgeschlagen</span>}
                      {x.status === "deleted" && <span className="text-amber-800 bg-amber-100 rounded-full px-2 py-0.5">abgelehnt → gelöscht</span>}
                      <code className="font-mono">{x.template}</code>
                      {x.error && <span className="text-red-600 truncate">— {x.error}</span>}
                    </div>
                  ))}
                </div>
                {tplResults.results.some((x) => x.status === "submitted") && (
                  <p className="text-[11px] text-slate-500 mt-2">
                    Meta prüft neue Vorlagen automatisch (meist innert Minuten bis Stunden). Status siehe WhatsApp Manager → Message Templates.
                  </p>
                )}
              </div>
            )}

            {/* Iter149: Test-Send an eine Nummer — deckt Meta Dev-Mode-Blocker (Fehler 131030) auf */}
            {cfg?.configured && (
              <div className="mt-5 border-t pt-4">
                <Label className="text-sm font-semibold text-slate-900">🧪 Test-Nachricht an Nummer senden</Label>
                <p className="text-[11px] text-slate-500 mt-0.5 mb-2 leading-relaxed">
                  Sendet eine echte WhatsApp-Nachricht via Meta an die angegebene Nummer.
                  Bei einem Fehler wird die exakte Meta-Antwort inkl. Fehlercode angezeigt
                  (z.B. <code className="bg-slate-100 px-1 rounded">131030</code> = Nummer nicht als Tester registriert).
                  <br />💡 <b>Freitext</b> kommt nur an, wenn dir die Nummer in den letzten 24h geschrieben hat —
                  schlägt das fehl, wird automatisch Metas freigegebene <code className="bg-slate-100 px-1 rounded">hello_world</code>-Vorlage
                  gesendet. Mit dem zweiten Button testest du die Vorlage direkt.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    placeholder="+41761234567"
                    value={sendPhone}
                    onChange={(e) => setSendPhone(e.target.value.trim())}
                    className="max-w-xs"
                    data-testid="wa-test-send-phone"
                  />
                  <Button
                    onClick={() => handleTestSend("text")}
                    disabled={sending || !sendPhone}
                    variant="outline"
                    data-testid="wa-test-send-btn"
                  >
                    {sending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                    Testnachricht senden
                  </Button>
                  <Button
                    onClick={() => handleTestSend("template")}
                    disabled={sending || !sendPhone}
                    variant="outline"
                    data-testid="wa-test-send-template-btn"
                  >
                    {sending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                    Vorlage senden (hello_world)
                  </Button>
                </div>
                {sendResult && (
                  <div
                    className={`mt-3 rounded border p-3 text-xs leading-relaxed ${
                      sendResult.ok
                        ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                        : "bg-rose-50 border-rose-200 text-rose-900"
                    }`}
                    data-testid="wa-test-send-result"
                  >
                    <div className="flex items-start gap-1.5">
                      {sendResult.ok ? (
                        <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      )}
                      <span className="font-medium">{sendResult.detail}</span>
                    </div>
                    {sendResult.meta_response && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-[11px] opacity-70 hover:opacity-100">
                          Roher Meta-Response anzeigen
                        </summary>
                        <pre className="mt-1 p-2 bg-white/60 rounded overflow-auto text-[10px] font-mono">
                          {JSON.stringify(sendResult.meta_response, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Card>

      {/* Iter228: Meta Message-Templates (24h-Regel) */}
      <Card className="p-5 bg-amber-50 border-amber-200" data-testid="wa-templates-card">
        <h3 className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
          <MessageCircle className="h-4 w-4" /> Meta Message-Templates (24h-Regel)
        </h3>
        <p className="text-xs text-amber-800 mt-1">
          Automatische Nachrichten (MWST-Erinnerung, Beleg-Anfragen, Digests) ausserhalb des
          24h-Fensters werden als <strong>Template</strong> gesendet. Reiche diese Vorlagen 1:1 im{" "}
          <a className="underline" href="https://business.facebook.com/wa/manage/message-templates/"
             target="_blank" rel="noreferrer">WhatsApp Manager → Message templates</a>{" "}
          ein (Kategorie <code className="bg-white px-1 rounded">UTILITY</code>, Sprache{" "}
          <code className="bg-white px-1 rounded">{tplCatalog?.language || "de"}</code>).
        </p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={loadMetaStatus} disabled={metaLoading} data-testid="wa-template-meta-status-btn">
            {metaLoading && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
            Freigabestatus bei Meta prüfen
          </Button>
          {metaStatus && (
            <span className="text-xs text-amber-900" data-testid="wa-template-meta-status-summary">
              {metaStatus.approved}/{metaStatus.total} Vorlagen von Meta freigegeben
              {metaStatus.payslip_template_name ? ` · Lohnabrechnung: ${metaStatus.payslip_template_name}` : ""}
            </span>
          )}
        </div>
        {tplCatalog?.templates?.map((t) => (
          <div key={t.name} className="mt-3 p-3 bg-white border border-amber-200 rounded" data-testid={`wa-template-${t.name}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <code className="text-xs font-mono font-semibold text-slate-800">{t.name}</code>
                {t.header === "DOCUMENT" && <span className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">PDF-Header</span>}
                {(() => {
                  const st = metaStatus?.templates?.find((x) => x.name === t.name)?.meta_status;
                  if (!st) return null;
                  const cls = st === "APPROVED" ? "bg-emerald-100 text-emerald-700" : st === "PENDING" ? "bg-amber-100 text-amber-800" : st === "REJECTED" ? "bg-red-100 text-red-700" : "bg-slate-200 text-slate-600";
                  const label = st === "APPROVED" ? "freigegeben ✓" : st === "PENDING" ? "in Prüfung" : st === "REJECTED" ? "abgelehnt" : "noch nicht eingereicht";
                  return <span className={`text-[10px] rounded-full px-2 py-0.5 ${cls}`} data-testid={`wa-template-meta-status-${t.name}`}>{label}</span>;
                })()}
              </div>
              <Button size="sm" variant="outline" className="h-6 text-[11px]"
                onClick={() => { navigator.clipboard.writeText(t.body); toast.success("Template-Text kopiert"); }}
                data-testid={`wa-template-copy-${t.name}`}>
                Text kopieren
              </Button>
            </div>
            {metaStatus?.templates?.find((x) => x.name === t.name)?.rejected_reason && (
              <p className="mt-1 text-[11px] text-red-700">Ablehnungsgrund: {metaStatus.templates.find((x) => x.name === t.name).rejected_reason}</p>
            )}
            <p className="mt-1.5 text-xs text-slate-700 whitespace-pre-wrap">{t.body}</p>
            <p className="mt-1 text-[10px] text-slate-500">
              Platzhalter: {t.params.map((p, i) => `{{${i + 1}}} = ${p}`).join(" · ")}
            </p>
          </div>
        ))}
        {outboundLog && outboundLog.length > 0 && (
          <details className="mt-3">
            <summary className="cursor-pointer text-[11px] text-amber-900 opacity-80 hover:opacity-100">
              Letzte automatische Sendeversuche ({outboundLog.length})
            </summary>
            <div className="mt-2 space-y-1" data-testid="wa-outbound-log">
              {outboundLog.map((e) => (
                <div key={e.id} className="text-[11px] font-mono bg-white border border-amber-100 rounded px-2 py-1 flex items-center gap-2">
                  {e.ok ? <CheckCircle2 className="h-3 w-3 text-emerald-600 flex-shrink-0" /> : <XCircle className="h-3 w-3 text-red-500 flex-shrink-0" />}
                  <span className="truncate">
                    {formatDateTime(e.created_at)} · {e.context} · {e.mode}
                    {e.within_24h ? " · im 24h-Fenster" : " · ausserhalb 24h"}
                    {e.error ? ` · ${e.error.slice(0, 60)}` : ""}
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}
      </Card>

      {/* Webhook-URL fürs Meta-Dashboard */}
      <Card className="p-5 bg-indigo-50 border-indigo-200" data-testid="wa-webhook-info">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-indigo-900">Webhook-URL für Meta Dashboard</h3>
            <p className="text-xs text-indigo-800 mt-1">
              Trage diese URL in <strong>Meta for Developers → Deine App → WhatsApp → Configuration → Webhooks</strong> ein,
              zusammen mit deinem <em>Verify Token</em>. Abonniere anschließend das Feld <code className="bg-white px-1 py-0.5 rounded text-[10px]">messages</code>.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <code className="text-xs font-mono bg-white border border-indigo-200 rounded px-2 py-1 text-slate-800 flex-1 truncate" data-testid="wa-webhook-url">
                {webhookUrl}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(webhookUrl);
                  toast.success("Webhook-URL kopiert");
                }}
                data-testid="wa-copy-webhook-url"
              >
                Kopieren
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Anleitung */}
      <Card className="p-5 bg-slate-50 border-slate-200">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
          <ExternalLink className="h-4 w-4" /> Wo bekomme ich die Meta-Zugangsdaten?
        </h3>
        <ol className="mt-2 text-xs text-slate-700 space-y-1.5 list-decimal list-inside">
          <li>
            Öffne <a className="text-indigo-600 hover:underline" href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer">developers.facebook.com/apps</a> und wähle deine App (oder lege eine neue vom Typ &bdquo;Business&ldquo; an).
          </li>
          <li>
            <strong>App Secret:</strong> App-Einstellungen → Basis → &bdquo;App-Secret anzeigen&ldquo;.
          </li>
          <li>
            <strong>Phone Number ID:</strong> WhatsApp → API-Setup → dropdown &bdquo;Von&ldquo; → Phone Number ID kopieren.
          </li>
          <li>
            <strong>Access Token:</strong> WhatsApp → API-Setup → temporärer Token (24h) oder in &bdquo;System Users&ldquo; einen permanenten Token erstellen mit <code className="bg-white px-1 rounded">whatsapp_business_messaging</code> + <code className="bg-white px-1 rounded">whatsapp_business_management</code>.
          </li>
          <li>
            <strong>Verify Token:</strong> Frei wählen (z.B. ein 32-stelliger Zufalls-String) — nur du und Meta müssen ihn kennen.
          </li>
          <li>
            Speichern → oben Webhook-URL kopieren → im Meta-Dashboard eintragen → <em>messages</em> abonnieren.
          </li>
        </ol>
      </Card>
    </div>
  );
}
